import { amber, cyan, green, red, slate, yellow } from './tokens';

/**
 * The semantic colour roles every Greasemap surface is built from.
 *
 * Components reference these names, never raw ramp values, so a theme swap needs no component
 * changes. Every role must exist in every theme -- `themes.spec.ts` enforces that.
 */
export interface ThemeColors {
  /** Page background. */
  readonly surface: string;
  /** Raised background: cards, sheets, headers. */
  readonly surfaceRaised: string;
  /** Background for a pressed or selected row. */
  readonly surfaceSelected: string;
  /** Hairlines and component outlines. */
  readonly border: string;
  /** Primary body text. */
  readonly text: string;
  /** Secondary text: captions, metadata, placeholders. */
  readonly textMuted: string;
  /** Brand colour for primary actions. */
  readonly accent: string;
  /** Text and icons placed on top of {@link accent}. */
  readonly onAccent: string;
  /** Destructive actions and error states. */
  readonly danger: string;
  /** Text and icons placed on top of {@link danger}. */
  readonly onDanger: string;
  /** Completion and confirmation. */
  readonly success: string;
  /** Caution. Never the only signal -- pair with an icon or label. */
  readonly warning: string;
  /** In-progress: a live run, an active timing feed, a connected device. */
  readonly live: string;
  /** Keyboard focus indicator. */
  readonly focusRing: string;
}

/** A complete named theme. */
export interface Theme {
  readonly name: 'light' | 'dark';
  readonly colors: ThemeColors;
}

/**
 * Light theme.
 *
 * The default for daylight use: the discovery map and any outdoor scoring screen. Foreground roles
 * are deliberately drawn from the darker end of each ramp so they clear 4.5:1 against white.
 */
export const lightTheme: Theme = {
  name: 'light',
  colors: {
    surface: '#ffffff',
    surfaceRaised: slate[50],
    surfaceSelected: slate[100],
    border: slate[500],
    text: slate[900],
    textMuted: slate[600],
    accent: amber[800],
    onAccent: '#ffffff',
    danger: red[700],
    onDanger: '#ffffff',
    success: green[700],
    warning: yellow[800],
    live: cyan[700],
    focusRing: amber[800],
  },
};

/**
 * Dark theme.
 *
 * Foreground roles come from the lighter end of each ramp for the same contrast reason, inverted.
 */
export const darkTheme: Theme = {
  name: 'dark',
  colors: {
    surface: slate[900],
    surfaceRaised: slate[800],
    surfaceSelected: slate[700],
    border: slate[500],
    text: slate[50],
    textMuted: slate[300],
    accent: amber[400],
    onAccent: slate[900],
    danger: red[400],
    onDanger: slate[900],
    success: green[400],
    warning: yellow[400],
    live: cyan[400],
    focusRing: amber[400],
  },
};

/** Every theme, by name. */
export const themes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

/** The name of a theme. */
export type ThemeName = keyof typeof themes;
